import { CreditCard, FileSpreadsheet, TrendingUp, Users, Wallet } from 'lucide-react';
import { useDashboardStats } from '@features/analytics/model/use-dashboard';
import { StatTile } from '@features/analytics/ui/StatTile';
import { ChartCard } from '@features/analytics/ui/ChartCard';
import { MrrChart } from '@features/analytics/ui/MrrChart';
import { FlowChart } from '@features/analytics/ui/FlowChart';
import { PlanRevenueChart } from '@features/analytics/ui/PlanRevenueChart';
import { SignupsChart } from '@features/analytics/ui/SignupsChart';
import { SubscriptionMix } from '@features/analytics/ui/SubscriptionMix';
import { EngagementCard } from '@features/analytics/ui/EngagementCard';
import {
  formatMoney,
  growthRate,
  sumCounts,
} from '@features/analytics/model/dashboard.types';
import { Alert } from '@shared/ui/Alert';
import { Spinner } from '@shared/ui/Spinner';
import type { ApiError } from '@shared/api/api-error';

export function DashboardPage() {
  const { data, isPending, isError, error } = useDashboardStats();

  if (isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }

  if (isError) {
    return <Alert tone="error">{(error as ApiError).message}</Alert>;
  }

  const { totals } = data;
  const totalSubscriptions = sumCounts(data.subscriptionsByStatus);
  const mrrGrowth = growthRate(totals.mrr, totals.previousMrr);

  // Depth of use among people who actually use the product, rather than diluted
  // across everyone who ever signed up.
  const budgetsPerBuilder =
    totals.usersWithBudgets > 0 ? (totals.budgets / totals.usersWithBudgets).toFixed(1) : '0';

  return (
    // The shell no longer scrolls, so this page owns its own scroll region.
    <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pb-2">
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <StatTile
          accent
          label="MRR"
          value={formatMoney(totals.mrr, totals.currency)}
          icon={Wallet}
          delta={mrrGrowth}
          hint={`from ${formatMoney(totals.previousMrr, totals.currency)} last month`}
        />
        <StatTile
          label="ARR"
          value={formatMoney(totals.arr, totals.currency)}
          icon={TrendingUp}
          hint="Run rate at current MRR"
        />
        <StatTile
          label="Paying customers"
          value={String(totals.payingCustomers)}
          icon={CreditCard}
          hint={`of ${totals.activeSubscriptions} live · ${totals.trials} on trial`}
        />
        <StatTile
          label="ARPU"
          value={formatMoney(totals.arpu, totals.currency, 2)}
          icon={Users}
          hint="Per paying customer, per month"
        />
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <ChartCard
          title="Recurring revenue"
          description="MRR at each month end, last 12 months"
          className="lg:col-span-2"
        >
          <MrrChart data={data.mrrByMonth} currency={totals.currency} />
        </ChartCard>

        <ChartCard title="MRR by plan" description="Where the revenue comes from">
          <PlanRevenueChart data={data.revenueByPlan} currency={totals.currency} />
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <ChartCard title="Won and lost" description="New vs cancelled subscriptions">
          <FlowChart data={data.subscriptionFlowByMonth} />
        </ChartCard>

        <ChartCard title="Subscription mix" description={`${totalSubscriptions} by status`}>
          <SubscriptionMix data={data.subscriptionsByStatus} />
        </ChartCard>

        <ChartCard title="Product health" description="Ratios worth watching">
          <EngagementCard totals={totals} />
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <ChartCard
          title="New users"
          description="Signups per month, last 12 months"
          className="lg:col-span-2"
        >
          <SignupsChart data={data.signupsByMonth} />
        </ChartCard>

        {/* A 2×2 so this column matches the chart's height instead of leaving a
            gap under two stretched tiles. */}
        <div className="grid grid-cols-2 gap-3">
          <StatTile
            label="Users"
            value={String(totals.users)}
            icon={Users}
            hint={`+${totals.newUsers30d} in 30 days`}
          />
          <StatTile
            label="Budgets"
            value={String(totals.budgets)}
            icon={FileSpreadsheet}
            hint={`${budgetsPerBuilder} per active builder`}
          />
          <StatTile
            label="Trials running"
            value={String(totals.trials)}
            icon={CreditCard}
            hint="Potential conversions"
          />
          <StatTile
            label="Builders"
            value={String(totals.usersWithBudgets)}
            icon={Users}
            hint="Users with ≥1 budget"
          />
        </div>
      </div>
    </div>
  );
}
